import { isValidPhoneNumber } from 'libphonenumber-js';
import * as yup from 'yup';

// Regular expressions for nodeId, tag, metadata, slug and context variable
export const slug = /^[\w][\w-]*$/i;

export const accountSlugRegex = /^[a-z\d](?:[a-z\d]|-(?=[a-z\d])){0,31}$/i;

/*
 * Matching examples:
 * $variable
 * $vari-able
 * $example.word
 * hello@world
 */
export const checkForPattern = new RegExp(/^\$[\w][\w-]*(?:\.[\w-]+)*|@[\w][\w-]*$/i);

/*
 * Matching examples:
 * {{$variable}}
 * {{$vari-able}}
 * {{$example.word}}
 * {{hello@world}}
 */
export const contextVariableWithBraces = /^{{\$[\w][\w-]*(?:\.[\w-]+)*}}$/i;

/*
 * Global version of contextVariableWithBraces for finding/replacing variables within text.
 * Unlike contextVariableWithBraces (which validates that an entire string IS a variable),
 * this pattern finds all variable occurrences within a larger string.
 *
 * Differences from contextVariableWithBraces:
 * - No anchors (^/$): matches variables anywhere in the string
 * - Global flag (g): finds all occurrences, not just the first
 */
export const contextVariableWithBracesGlobal = new RegExp(
  contextVariableWithBraces.source.slice(1, -1),
  'gi'
);

/*
 * http://www.example.com
 * viber://chat?contact=123456789
 * https://example.com/{{\$dynamicPath}}/resource
 * {{\$customScheme}}://{{\$dynamicDomain}}/{{\$dynamicPath}}
 */
export const contextUrl =
  /^(https|ftp|viber|erparent|({{\$[\w][\w-]*(?:\.[\w-]+)*}})):\/\/(((([\w_-]+(?:(?:\.[\w_-]+)?))|({{\$[\w][\w-]*(?:\.[\w-]+)*}}))((([\w.,@?^=%&:/~+#-]|({{\$[\w][\w-]*(?:\.[\w-]+)*}}))*([\w@?^=%&/~+#-]|({{\$[\w][\w-]*(?:\.[\w-]+)*}})))?({{\$[\w][\w-]*(?:\.[\w-]+)*}})?))|({{\$[\w][\w-]*(?:\.[\w-]+)*}}))$/i;

// url pattern of yup validator
export const urlPattern =
  /^(https|ftp|viber):\/\/(?:([\p{L}\p{N}._~-]+):([^@]+)@)?([\p{L}\p{N}._~-]+(?:\.[\p{L}\p{N}._~-]+)*)(:[0-9]+)?(\/[\p{L}\p{N}._~\-.,@?^=%&:/+()'#]*)?(\?[\p{L}\p{N}._~\-.,@?^=%&:/+()'#]*)?(#[\p{L}\p{N}._~\-.,@?^=%&:/+()'#]*)?$/u;

export const noUrlPattern =
  /(?:https?|ftp|file|data|javascript|mailto|tel|sms|blob):\/\/[^\s/$.?#].[^\s]*/i;

export const urlWithoutProtocolPattern =
  /^((?:[\w_-]+(?:(?:\.[\w_-]+)?))([\w.,@?^=%&:/~+#-]*[\w@?^=%&/~+#-])?)?$/i;

// email pattern of yup validator
export const emailPattern =
  // eslint-disable-next-line no-control-regex, no-useless-escape
  /^((([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+(\.([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+)*)|((\x22)((((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(([\x01-\x08\x0b\x0c\x0e-\x1f\x7f]|\x21|[\x23-\x5b]|[\x5d-\x7e]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(\\([\x01-\x09\x0b\x0c\x0d-\x7f]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]))))*(((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(\x22)))@((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]))){2,}$/i;

// E.164 phone number format: + followed by country code (1-3 digits) and subscriber number
// Total: 4-15 digits after the + (minimum of 4 to ensure valid format)
export const e164PhonePattern = /^\+[1-9]\d{3,14}$/;

// SIP/SIPS URI pattern - matches common SIP URI formats
// Examples: sip:user@example.com, sips:+15551234567@pbx.com, sip:1001@192.168.1.1:5060
// Supports: domain names, IPv4 addresses, IPv6 addresses (in brackets), E.164 numbers in user part
// More permissive to handle real-world SIP URIs per RFC 3261
export const sipUriPattern =
  /^sips?:([a-zA-Z0-9._%\-&+=$,;?/]+)@([a-zA-Z0-9.-]+|\[[a-fA-F0-9:.]+])(:(\d{1,5}))?$/i;

/*
 * Matching examples:
 * $a
 * $example
 * $break-word
 * $wordA.wordB
 */
export const contextRefRegExp = new RegExp(/^\$[\w][\w-]*(?:\.[\w-]+)*$/i);

/*
 * Matching examplpes:
 * a
 * example
 * break-word
 * wordA.wordB
 */
export const saveAsRefRegExp = new RegExp(/^[\w][\w-]*(?:\.[\w-]+)*$/i);

export const spreadsheetIdPattern = /^[a-zA-Z0-9-_]+$/i;

// It is based on the regex slug, but allows empty strings
export const tagPattern = /^([\w][\w-]*|)$/i;

/*
 * Matching examples:
 * $variable
 * $variable.secondPart
 * $variable.secondPart.thirdPart
 * @contextName
 * @context-name
 */
export const entityOrContext = new RegExp(/^\$[\w][\w-]*(?:\.[\w-]+)*$|^@[\w][\w-]*$/i);

// Validation helper functions for patterns
export const isValidAccountSlug = (slug: string): boolean => accountSlugRegex.test(slug);

export const isValidSlug = (text: string): boolean => slug.test(text);

// ============================================
// Phone Validation (Yup + react-hook-form)
// ============================================

/**
 * Returns an i18n error key when the phone number is invalid, or null when valid.
 * Accepts E.164 ("+15551234567") or raw digits ("15551234567").
 */
export const getPhoneDigitError = (value: string): string | null => {
  if (!value) {
    return null;
  }
  const normalized = value.startsWith('+') ? value : `+${value}`;
  return isValidPhoneNumber(normalized) ? null : 'validation.phone.invalid';
};

/**
 * Yup validation for phone numbers using libphonenumber-js.
 * Accepts E.164 format ("+15551234567") as output by PhoneInput.
 */
export const phoneValidation = yup
  .string()
  .trim()
  .test('phone-valid', 'validation.phone.invalid', (value) => {
    if (!value) {
      return true;
    }
    return isValidPhoneNumber(value);
  });

/**
 * Normalize phone to E.164 format (add + if missing).
 * Strips non-digits and prepends + prefix.
 */
export const normalizePhone = (phone: string): string => {
  if (!phone) {
    return '';
  }
  const digits = phone.replace(/\D/g, '');
  return digits ? `+${digits}` : '';
};

// ============================================
// Character Limits
// ============================================

/** SMS message character limit (single segment) */
export const SMS_CHARACTER_LIMIT = 160;

/** Email subject line character limit */
export const EMAIL_SUBJECT_CHARACTER_LIMIT = 256;

/** Email body character limit */
export const EMAIL_BODY_CHARACTER_LIMIT = 1600;

/** Voice message character limit */
export const VOICE_CHARACTER_LIMIT = 1600;

// ============================================
// Shared Outbound Messaging Validators
// ============================================

/**
 * Returns true if all template variable parameters are filled (non-empty after trim).
 * Used by NewMessageModal and campaigns for WhatsApp template validation.
 */
export const areAllTemplateVariablesFilled = (
  params: Array<{ name?: unknown }> | undefined | null
): boolean => {
  if (!params || params.length === 0) {
    return true;
  }
  return params.every((param) => {
    if (param?.name === undefined || param?.name === null) {
      return false;
    }
    return String(param.name).trim() !== '';
  });
};

/**
 * Validates recipient format based on channel type.
 * Ensures consistent phone/email validation across NewMessageModal and campaigns.
 */
export const isValidRecipientFormat = (
  recipientType: 'phone' | 'email',
  value: string
): boolean => {
  if (!value?.trim()) {
    return false;
  }
  if (recipientType === 'email') {
    return emailPattern.test(value);
  }
  return e164PhonePattern.test(normalizePhone(value));
};
